import { Inject } from 'vue-di-container';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Toaster } from '../../../Services/toast';
import ValidationService from '../../../Services/ValidationService';
import { PreApprovedTemplateModel } from '../PreApprovedTemplateModel';
import PreApprovedTemplateService from '../PreApprovedTemplateService';
import WithRender from './AddPreApprovedTemplate.html';
import { DropdownModel } from '../../Provider/ProviderModel';

@WithRender
@Component({
    components: {},
    diProvide: [
        PreApprovedTemplateService,
    ],
})
export default class AddPreApprovedTemplate extends Vue {
    @Inject(PreApprovedTemplateService) public preApprovedTemplateService!: PreApprovedTemplateService;
    @Inject(ValidationService) public validationService!: ValidationService;

    @Prop({ type: String, default: 'Add' })
    public mode: string | any;

    @Prop({ default: '' })
    public id!: string;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    public confirmDialog: boolean = false;
    public classificationList: DropdownModel[] = [];
    public templateVariableExamples: string[] = [];
    private lastValidatedTemplateContent: string = '';
    private isTemplateContentMutationAllowed: boolean = false;

    public model: PreApprovedTemplateModel = {
        id: '',
        name: '',
        description: '',
        templateContent: '',
        classification: 0,
        createdDate: '',
    };

    private getDefaultVariableExampleValue(): string {
        return '';
    }

    public created() {
        this.classificationList = [
            { id: 1, name: this.$t('shared.Advertising').toString() },
            { id: 2, name: this.$t('shared.Service').toString() },
            { id: 3, name: this.$t('shared.Warning').toString() },
            { id: 5, name: this.$t('shared.Awareness').toString() },
        ];
    }

    private setTemplateContentWithMutation(content: string): void {
        this.isTemplateContentMutationAllowed = true;
        this.model.templateContent = content;
        this.lastValidatedTemplateContent = content || '';
        this.$nextTick(() => {
            this.isTemplateContentMutationAllowed = false;
        });
    }



    private extractVariablePlaceholders(content: string): string[] {
        return (content || '').match(/#Var\d+#/gi) || [];
    }

    public addVariable() {
        const nextIndex = this.templateVariableExamples.length + 1;
        const updatedContent = `${this.model.templateContent || ''}#Var${nextIndex}#`;

        this.setTemplateContentWithMutation(updatedContent);
        this.ensureVariableExampleCount(nextIndex);

        this.$set(
            this.templateVariableExamples,
            nextIndex - 1,
            this.getDefaultVariableExampleValue(),
        );
    }

    public deleteVariable(index: number) {
        this.templateVariableExamples.splice(index - 1, 1);

        let content = this.model.templateContent || '';
        content = content.replace(new RegExp(`#Var${index}#`, 'gi'), '');

        let currentMax = this.templateVariableExamples.length + 1;
        for (let i = index + 1; i <= currentMax; i++) {
            content = content.replace(new RegExp(`#Var${i}#`, 'gi'), `#Var${i - 1}#`);
        }

        this.setTemplateContentWithMutation(content);
    }

    public setVariableExample(index: number, value: string) {
        this.ensureVariableExampleCount(index);
        this.$set(this.templateVariableExamples, index - 1, value);
    }

    private ensureVariableExampleCount(index: number) {
        while (this.templateVariableExamples.length < index) {
            this.templateVariableExamples.push('');
        }
    }

    public get templateContentTotalCharacterCount(): number {
        return (this.model.templateContent || '').replace(/#Var\d+#/gi, '').length;
    }

    public get templateContentError(): string | null {
        if (!this.model.templateContent) return null;

        const trimmedContent = this.model.templateContent.trim();
        const startsWithVar = /^#Var\d+#/i.test(trimmedContent);
        const endsWithVar = /#Var\d+#$/i.test(trimmedContent);

        if (startsWithVar || endsWithVar) {
            return this.$t('shared.templateVariableBoundaryInvalid').toString();
        }
        return null;
    }

    public get templateDescriptionError(): string {
        const description = (this.model.description || '').trim();
        if (!description) return '';
        if (description.length < 3) {
            return this.$t('shared.templateDescriptionMinLength').toString();
        }
        if (description.length > 160) {
            return this.$t('shared.templateDescriptionMaxLength').toString();
        }
        return '';
    }

    @Watch('model.templateContent')
    public onTemplateContentChanged(newContent: string) {
        if (this.isTemplateContentMutationAllowed) {
            this.lastValidatedTemplateContent = newContent || '';
            return;
        }

        const previousContent = this.lastValidatedTemplateContent || '';
        const previousVariables = this.extractVariablePlaceholders(previousContent);
        const nextVariables = this.extractVariablePlaceholders(newContent || '');

        const variablesPreserved = previousVariables.length === nextVariables.length
            && previousVariables.every((token, index) => token.toLowerCase() === (nextVariables[index] || '').toLowerCase())
            && previousVariables.every((token) => (newContent || '').includes(token));

        if (!variablesPreserved) {
            this.isTemplateContentMutationAllowed = true;
            this.model.templateContent = previousContent;
            this.$nextTick(() => {
                this.isTemplateContentMutationAllowed = false;
            });
            return;
        }

        this.lastValidatedTemplateContent = newContent || '';
    }

    public async mounted() {
        this.$emit('childinit', this.layoutSup);

        this.lastValidatedTemplateContent = this.model.templateContent || '';
    }

    public get isValid(): boolean {
        const areVariablesValid = this.templateVariableExamples.every(
            value => value && value.trim() !== ''
        );

        return !!this.model.name
            && !!this.model.templateContent
            && !!this.model.classification
            && !this.templateDescriptionError
            && !this.templateContentError
            && areVariablesValid; 
    }

    public showConfirmDialog() {
        if (this.isValid) this.confirmDialog = true;
    }

    public hideConfirmDialog() {
        this.confirmDialog = false;
    }

    public async save() {
        const structuredPayload = {
            text: this.model.templateContent,
            variables: this.templateVariableExamples
        };

        const finalModel = {
            ...this.model,
            templateContent: JSON.stringify(structuredPayload)
        };

        const response = await this.preApprovedTemplateService.post(finalModel);

        if (response.isSuccess && response.result) {
            Toaster.success(
                this.$t('shared.addSuccessfully').toString());
            this.$router.push({ name: 'PreApprovedTemplates' });
        } else
        {
            Toaster.error(response.errorMessage);
        }
        this.confirmDialog = false;
    }
}